import { appendIf } from '@btime/helpers'
import { assoc, compose, dissoc, pick } from 'ramda'
import { gql, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { generalClient } from '../../../../utils/apollo/api'
import { useCloseModal, useModalData } from '../../../../components/modals/ModalsContext'

const MUTATION = gql`
  mutation UpsertScreen($input: ScreenInput) {
    upsertScreen(input: $input) {
      id
      name
    }
  }
`

const cleanUserGroups = userGroups => {
  if (!userGroups || !userGroups.length) {
    return []
  }
  const groupIds = userGroups.map(group => group.id)

  return groupIds
}

const cleanRequest = (form, isCloning) => {
  return compose(
    pick(['id', 'name', 'screen', 'data', 'screen', 'userGroups']), //
    item => (isCloning ? dissoc('id', item) : item),
    item =>
      assoc(
        'data',
        JSON.stringify({
          ...form.data,
          configuration: {
            icon: form?.data?.configuration?.icon,
            color: form?.data?.configuration?.color,
            embedLink: form?.data?.configuration?.embedLink
          }
        }),
        item
      ),
    item => assoc('userGroups', cleanUserGroups(item.userGroups), item),
    item => assoc('screen', 'service_orders', item)
  )(form)
}

const isValidUrl = url => {
  try {
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

const validate = (form, setErrors, t) => {
  const errors = [
    ...appendIf(!form.name, { path: ['name'], message: t('processes.validation.required') }),
    ...appendIf(!form.name, { path: ['screen'], message: t('processes.validation.required') }),
    ...appendIf(!form.data?.configuration?.color, {
      path: ['data', 'configuration', 'color'],
      message: t('processes.validation.required')
    }),
    ...appendIf(!form.data?.configuration?.icon, {
      path: ['data', 'configuration', 'icon'],
      message: t('processes.validation.required')
    }),
    ...appendIf(form.data?.configuration?.embedLink && !isValidUrl(form.data?.configuration?.embedLink), {
      path: ['data', 'configuration', 'embedLink'],
      message: t('processes.validation.invalidUrl')
    })
  ]

  setErrors(errors)
  return errors.length === 0
}

export const useSaveProcesses = () => {
  const { t } = useTranslation('processes')
  const closeModal = useCloseModal()
  const { isCloning } = useModalData()
  const [request, state] = useMutation(MUTATION, { refetchQueries: ['Screens'], client: generalClient })

  const handleRequest = (form, actions) => {
    const input = cleanRequest(form, isCloning)
    const { setErrors } = actions
    const isValid = validate(form, setErrors, t)

    if (isValid) {
      return request({ variables: { input } }).then(resp => {
        if (!resp.errors) {
          closeModal()
        }
      })
    }
  }

  return [handleRequest, state]
}
